import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { useCallback, useState } from "react";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WACalendarOverviewPreview } from "./WACalendarOverviewPreview";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { WidgetCalendarOverviewSettings } from "./WidgetCalendarOverviewSettings";
import { WACalendarOverviewContext } from "./WACalendarOverviewContext";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useWidgetCalendarPreviewOverviewQuery } from "./useWidgetCalendarPreviewOverviewQuery";
import { WSCalendarOverviewQuery } from "./__generated__/WSCalendarOverviewQuery.graphql";

import { WSCalendarOverviewMutation } from "./__generated__/WSCalendarOverviewMutation.graphql";
import { useFocusEffect } from "@react-navigation/native";
import { WidgetScreen } from "../../common/WidgetScreen";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

type Props = UserLoggedInNavigatorScreenProps<"WSCalendarOverview">;

function InnerWSCalendarOverview(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WSCalendarOverviewQuery>(
    graphql`
      query WSCalendarOverviewQuery($id: ID!) {
        widgetCalendarOverview(id: $id) {
          id
          calendars(includeExternal: true) {
            id
          }
        }
      }
    `,
    {
      id: props.route.params.widgetId,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );
  const [calendarIds, setCalendarIds] = useState<string[]>(
    data.widgetCalendarOverview?.calendars.map(({ id }) => id) ?? []
  );
  const [previewQuery, loadPreviewQuery] =
    useWidgetCalendarPreviewOverviewQuery({
      storecastId: props.route.params.storecastId,
    });

  useFocusEffect(
    useCallback(() => {
      loadPreviewQuery(calendarIds);
    }, [loadPreviewQuery, calendarIds])
  );

  const onSelectCalendarIds = useCallback(
    (calendarIds: string[]) => {
      setCalendarIds(calendarIds);
      loadPreviewQuery(calendarIds);
    },
    [loadPreviewQuery, setCalendarIds]
  );

  const [updateWidgetCalendarOverviewMutation, loading] =
    useMutation<WSCalendarOverviewMutation>(graphql`
      mutation WSCalendarOverviewMutation(
        $storecastId: ID!
        $id: ID!
        $input: WidgetCalendarOverviewUpdateInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          updateWidgetCalendarOverview(id: $id, update: $input) {
            id
            ...WidgetCalendarOverview_widget
            calendars(includeExternal: true) {
              id
            }
          }
        }
      }
    `);

  const onSave = useCallback(() => {
    updateWidgetCalendarOverviewMutation({
      variables: {
        storecastId: props.route.params.storecastId,
        id: props.route.params.widgetId,
        input: {
          setCalendarIds: calendarIds,
        },
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
      onError: (error) => {
        console.log("error", error);
      },
    });
  }, [
    props.navigation,
    updateWidgetCalendarOverviewMutation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    calendarIds,
  ]);

  return (
    <WidgetScreen
      settings={
        <WACalendarOverviewContext.Provider
          value={{
            calendarIds,
            setCalendarIds: onSelectCalendarIds,
          }}
        >
          <WidgetCalendarOverviewSettings
            onClose={() => props.navigation.goBack()}
          />
        </WACalendarOverviewContext.Provider>
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {previewQuery ? (
            <WACalendarOverviewPreview query={previewQuery} />
          ) : null}
        </WidgetScreenPreview>
      }
    />
  );
}

export const WSCalendarOverview = withWebModal((props: Props) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWSCalendarOverview {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}, true);
